// tabs/ability-row.ts — shared ability-row/section builders for the caster-sheet ability
// tables. Extracted from ability-section.ts so the Abilities tab (ability-section.ts), the
// weapons attack tables (weapon-abilities.ts), the passive tree (passive-abilities.ts), and
// the single-section creature mount all render IDENTICAL rows from ONE code path.
//
// Each row: ability button (ability-button.ts) + name (with the variable charge / mana-dice /
// summon / shapeshift inputs the button reads) + source icon (requires-level / requires-class-
// level gate as a pill ON the icon — no standalone level column) + charges (input + recharge/
// reset chip + the roll-damage effect button). Ports of render_abilities() /
// render_abilities_table() in includes/functions/encounter_creatures/render_abilities_inc.php
// and includes/functions/janus/core/list_display_helpers.php.

import type { CachedCaster } from '../types';
import { fullNodeById } from '../../shared/array-types';
import { findFullNestedItem } from '../../object-card/find-cached-item';
import { decodeHtmlEntitiesForPlainText } from '../../shared/decode-html-entities';

type UnknownRecord = Record<string, unknown>;
function recordOf(value: unknown): UnknownRecord | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as UnknownRecord
        : null;
}
function scalarCharge(value: unknown, fallback: number | string): number | string {
    return typeof value === 'number' || typeof value === 'string' ? value : fallback;
}

// v21 bare-ref name resolution: findFullNestedItem returns FULL defs only, so a layerless
// catalogized ability (identity-only catalog row {item_name,item_type}) missed it and rendered
// "Unknown". Walk the cached roots' .catalog for the identity directly. Session-memoized
// on hits (catalog identities are static). Catalog rows come in two shapes: identity-only
// {item_name,item_type} and full defs {items:{...},item_layer_types,...}.
const _catIdentityMemo = new Map<string, { name: string; type: string }>();
export function catalogIdentityById(id: string): { name: string; type: string } | null {
    const key = id.toUpperCase();
    const hit = _catIdentityMemo.get(key);
    if (hit !== undefined) return hit;
    const ft = window.FateTide;
    // encounterCache included: member blobs lose their own .catalog (unioned to the encounter root).
    for (const m of [ft?.casterCache, ft?.objectCache, ft?.encounterCache]) {
        if (!(m instanceof Map)) continue;
        for (const rootValue of m.values()) {
            const cat = recordOf(recordOf(rootValue)?.catalog);
            if (!cat) continue;
            const e = cat[id] ?? cat[key] ?? cat[id.toLowerCase()];
            const eRecord = recordOf(e);
            if (!eRecord) continue;
            const src = recordOf(eRecord.items) ?? eRecord;
            const col = (k: string): string => {
                const v = Array.isArray(src[k]) ? src[k][0] : src[k];
                return typeof v === 'string' ? v : '';
            };
            const ident = { name: col('item_name'), type: col('item_type') };
            if (ident.name !== '' || ident.type !== '') { _catIdentityMemo.set(key, ident); return ident; }
        }
    }
    return null;
}
// items.item_name is persisted HTML-ENCODED ("Adon&#039;s Chosen"), so every catalog identity read
// carries the entity. Decode at BOTH shared name funnels (here and abilityEntryName) — the callers
// all feed the result to escapeHtml, which would otherwise re-encode the entity's & into
// &amp;#039; and print a literal "&#039;" on screen. escapeHtml stays the sole encoder.
export function catalogNameById(id: string): string {
    return decodeHtmlEntitiesForPlainText(catalogIdentityById(id)?.name ?? '');
}

// Essences live in additions.php Essence::CORE/::EXTRA, NOT in root.catalog — so catalogNameById
// misses them, and a user_choices essence pick (choice_type user_choice_*_essence_feat /
// _essence_ability / …, whose `choice` is an essence UUID) would render its raw choice UUID
// instead of "Body" / "Mind" / …. Keep this in sync with ESSENCE_META in
// info-panel/essence-breakdown.ts (the other client-side copy of these 6 stable ids).
const ESSENCE_NAME_BY_ID: Record<string, string> = {
    '22918408-67CC-4093-80E8-BF6FA8915C21': 'Body',
    '63ABC96E-3804-4E1F-B38D-AABA3237B2D8': 'Mind',
    '72F93730-134F-4CA2-8FC4-F2915F20CAC6': 'Soul',
    '86792A8B-2070-4144-9C81-0D85706131DF': 'Spirit',
    'E769E451-65EE-3628-1A78-D12C9A3FF02B': 'Fortune',
    '7B7E8F97-6843-57CF-422C-5930CFA98B06': 'Luck',
};
export function essenceNameById(id: string): string {
    return ESSENCE_NAME_BY_ID[String(id ?? '').toUpperCase()] ?? '';
}

/** Blob-first source identity (name + type), falling back to the root-catalog identity when
 *  the source def was thinned OUT of the caster blob entirely (catalogization: class /
 *  ancestry / subclass defs now live only in root.catalog). Without the fallback every such
 *  group/tooltip degrades to the literal 'Source'. iconId/inv ride along from the blob entry
 *  (inventory rows only — those never thin to the catalog). */
export function sourceIdentity(caster: CachedCaster | undefined, id: string): { name: string; type: string; iconId?: string } {
    const info = blobSourceIndex(caster).types[id.toLowerCase()];
    let name = typeof info?.name === 'string' ? info.name : '';
    let type = String(info?.type ?? '');
    if (name === '' || type === '') {
        const cat = catalogIdentityById(id);
        if (cat) {
            if (name === '') name = cat.name;
            if (type === '') type = cat.type;
        }
    }
    // Provenance labels (passive crumbs, action-bar weapon-group labels) are display text — decode the
    // HTML-encoded name once here, exactly like catalogNameById/abilityEntryName, so the consumers'
    // escapeHtml is the only encoder. Safe for the iconMap(..., tooltip) consumers now that iconMap
    // escapes its `alt` (core-helpers.ts); it previously did not, which is why this was left encoded.
    return { name: decodeHtmlEntitiesForPlainText(name), type, iconId: info?.iconId };
}
import { generateAbilityButton, statusChoiceReaders, sheetChargeBudget, sheetManaBudget } from '../ability-button';
import { casterStatusChoiceIds, choiceEntryUuid, CHOICE_APPLIER_LAYERS } from '../../janus-engine/ability/statuses/caster-status-choice';
import { num } from '../read-helpers';
import { abilityEffectButton } from './ability-effect-button';
import { chargeAbilityIcon } from '../../shared/spell-display';

type AbilityRowWindow = Window & {
    openItemInfo?: (itemId: string) => unknown;
    openItemInfoValue?: (selectId: string) => unknown;
    handleAbilityChoiceChange?: (element: HTMLElement) => unknown;
    changeCharges?: (abilityId: string, inputId: string) => unknown;
};

function abilityRowAttrs(action: string, attrs: Record<string, string> = {}): string {
    const pairs = [`data-ft-ability-row-action="${escapeHtml(action)}"`];
    for (const [key, value] of Object.entries(attrs)) {
        pairs.push(`data-ft-${key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}="${escapeHtml(value)}"`);
    }
    return pairs.join(' ');
}

export function abilityInfoAttrs(itemId: string): string {
    return abilityRowAttrs('open-info', { itemId });
}

function abilityOpenValueAttrs(selectId: string): string {
    return abilityRowAttrs('open-info-value', { selectId });
}

function abilityChoiceChangeAttrs(): string {
    return abilityRowAttrs('ability-choice-change');
}

function abilityChargeChangeAttrs(onchange: string): string {
    const match = /^changeCharges\('([^']+)','([^']+)'\);$/.exec(onchange.trim());
    if (!match) return '';
    const abilityId = match[1];
    const inputId = match[2];
    return abilityId && inputId ? abilityRowAttrs('change-charges', { abilityId, inputId }) : '';
}

document.addEventListener('click', (event) => {
    const el = event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-ft-ability-row-action]')
        : null;
    if (!el || !document.contains(el)) return;
    const action = el.dataset.ftAbilityRowAction ?? '';
    const w = window as AbilityRowWindow;
    if (action === 'open-info') {
        const itemId = el.dataset.ftItemId ?? '';
        if (itemId !== '') {
            event.preventDefault();
            w.openItemInfo?.(itemId);
        }
    }
});

document.addEventListener('change', (event) => {
    const el = event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-ft-ability-row-action]')
        : null;
    if (!el || !document.contains(el)) return;
    const action = el.dataset.ftAbilityRowAction ?? '';
    const w = window as AbilityRowWindow;
    if (action === 'open-info-value') {
        const selectId = el.dataset.ftSelectId ?? '';
        if (selectId !== '') w.openItemInfoValue?.(selectId);
    } else if (action === 'ability-choice-change') {
        w.handleAbilityChoiceChange?.(el);
    } else if (action === 'change-charges') {
        const abilityId = el.dataset.ftAbilityId ?? '';
        const inputId = el.dataset.ftInputId ?? '';
        if (abilityId !== '' && inputId !== '') w.changeCharges?.(abilityId, inputId);
    }
});

// ── entry accessors (ports of ability_entry_* in encounter_link_and_ability_builders.php) ──
export function abilityNest(entry: unknown): UnknownRecord | string | null {
    // Bare-UUID-STRING entry (Stage-3 pure ref — e.g. the Paladin smites Atonement / Penance /
    // Holy Reckoning): resolve to its full def via the global finder so LAYER reads work —
    // requires_class_level for abilityRequiredLevelSort (without this every bare-string attack
    // sorted as the default 1 and the class group rendered out of level order), plus charges /
    // gates. PARITY with the dict-ref branch below; abilityEntryId/abilityEntryName handle the
    // string directly BEFORE calling here, so name/id authority is unaffected. Falls back to the
    // bare string when the def isn't cached yet (callers still guard for a non-object nest).
    if (typeof entry === 'string' && entry !== '') {
        return findFullNestedItem(entry) ?? entry;
    }
    const entryRecord = recordOf(entry);
    if (entryRecord) {
        // v23 #5: bare-UUID refs renamed item_arr → item_id. Accept the renamed slot whenever no
        // item_arr key exists — MIRRORS PHP _ft_ability_entry_layer_src, which has no item_name
        // condition. (v34 made this load-bearing: a wrapper row with a KEPT divergent item_name
        // + elided charges metadata must still resolve its def for the layer fallbacks. Name
        // authority is unaffected — abilityEntryName reads the flat item_name FIRST regardless.)
        const ia = entryRecord.item_arr
            ?? (!('item_arr' in entryRecord) && typeof entryRecord.item_id === 'string'
                ? entryRecord.item_id : undefined);
        if (ia && typeof ia === 'object') return recordOf(ia);
        // v21 pure-ref form: item_arr is a bare UUID pointing at the root catalog — resolve via
        // the cache/catalog finder. On a miss (cache not filled yet) synthesize a minimal identity
        // node so abilityEntryId still answers; name/layers degrade gracefully until the cache fills.
        if (typeof ia === 'string' && ia !== '') {
            // Direct import (NOT window.ftFindFullNestedItem — that attach belongs to another
            // bundle and may not exist yet at sheet-render time).
            const full = findFullNestedItem(ia);
            return full ?? { items: { item_id: ia, item_name: catalogNameById(ia) || undefined } };
        }
    }
    return entryRecord ?? null;
}
export function abilityEntryId(entry: unknown): string {
    if (typeof entry === 'string') return entry;
    const entryRecord = recordOf(entry);
    if (!entryRecord) return '';
    if (typeof entryRecord.ability_id === 'string' && entryRecord.ability_id !== '') return entryRecord.ability_id;
    const id = ftItemsCol(abilityNest(entry), 'item_id');
    return typeof id === 'string' ? id : '';
}
/** THE ability-name funnel for the whole sheet (Abilities tab, weapon attack tables, passive tree).
 *  Decodes the HTML-encoded item_name here so every caller's escapeHtml is the only encoder — see the
 *  note on catalogNameById.
 *  The raw resolver below reads catalogIdentityById directly rather
 *  than catalogNameById so the shared bounded decoder owns all historical encoding passes. */
export function abilityEntryName(entry: unknown): string {
    return decodeHtmlEntitiesForPlainText(abilityEntryNameRaw(entry));
}
function abilityEntryNameRaw(entry: unknown): string {
    // Stripped bare-UUID ref (Stage 3): resolve to the full copy's name. The dedupe keeps a full
    // copy in-blob, and ftFindFullNestedItem walks all caches incl. root.catalog full-defs.
    if (typeof entry === 'string') {
        const full = findFullNestedItem(entry);
        const nm = full ? ftItemsCol(full, 'item_name') : '';
        if (typeof nm === 'string' && nm !== '') return nm;
        const catNm = catalogIdentityById(entry)?.name ?? ''; // identity-only catalog row (layerless ability)
        return catNm !== '' ? catNm : 'Unknown';
    }
    const entryRecord = recordOf(entry);
    if (!entryRecord) return 'Unknown';
    if (typeof entryRecord.item_name === 'string' && entryRecord.item_name !== '') return entryRecord.item_name;
    const nm = ftItemsCol(abilityNest(entry), 'item_name');
    if (typeof nm === 'string' && nm !== '') return nm;
    // v21 bare-ref entry whose full def isn't findable (layerless → identity-only catalog row).
    const refId = (typeof entryRecord.item_arr === 'string') ? entryRecord.item_arr : abilityEntryId(entry);
    const catNm = refId ? (catalogIdentityById(refId)?.name ?? '') : '';
    return catNm !== '' ? catNm : 'Unknown';
}
export function abilityEntryCharges(entry: unknown, dflt: number | string = 'NA'): number | string {
    const entryRecord = recordOf(entry);
    if (entryRecord) {
        const charges = recordOf(entryRecord.charges);
        if (charges && 'current' in charges) return scalarCharge(charges.current, dflt);
        if ('ability_charges' in entryRecord) return scalarCharge(entryRecord.ability_charges, dflt);
        const nested = recordOf(entryRecord.item_arr)?.ability_charges;
        if (Array.isArray(nested) && nested[0] !== undefined) return nested[0];
        // Full-nested entry (entry IS the ability item_arr — un-finalized/non-encounter
        // buckets): there is no live 'charges' sub-array, so surface the declared max from
        // the ability_charges LAYER (carrier-aware read). Mirrors the PHP
        // ability_entry_charges() item_arr.ability_charges fallback. No live state → full.
    }
    // Catalog-as-store bare UUIDs (notably static creature natural-weapon attacks) have no
    // entryRecord, but abilityNest resolves their full definition from the caster/root catalog.
    // There is no live encounter state on this shape, so the declared maximum is the correct
    // read-only value. Live wrapper rows above still win through charges.current.
    const layered = ftLayerValue(abilityNest(entry), 'ability_charges');
    if (layered !== null && layered !== undefined && layered !== '') return layered as number | string;
    return dflt;
}
export function abilityEntryMaxCharges(entry: unknown, dflt: number | string = 'NA'): number | string {
    const entryRecord = recordOf(entry);
    if (entryRecord) {
        const charges = recordOf(entryRecord.charges);
        if (charges && 'max' in charges) return scalarCharge(charges.max, dflt);
        if ('max_ability_charges' in entryRecord) return scalarCharge(entryRecord.max_ability_charges, dflt);
        const nested = recordOf(entryRecord.item_arr)?.ability_charges;
        if (Array.isArray(nested) && nested[0] !== undefined) return nested[0];
        // Full-nested entry: read the declared max from the ability_charges LAYER.
        // Mirrors the PHP ability_entry_max_charges() item_arr.ability_charges fallback.
    }
    const layered = ftLayerValue(abilityNest(entry), 'ability_charges');
    if (layered !== null && layered !== undefined && layered !== '') return layered as number | string;
    return dflt;
}
export function abilityEntryRechargeBegin(entry: unknown): number {
    const entryRecord = recordOf(entry);
    if (entryRecord) {
        const rechargeBegin = recordOf(entryRecord.charges)?.recharge_begin;
        if (rechargeBegin !== undefined) return num(rechargeBegin);
        if (entryRecord.recharge_range_begin !== undefined) return num(entryRecord.recharge_range_begin);
    }
    const v = ftLayerValue(abilityNest(entry), 'recharge_range_begin');
    if (v !== null && v !== undefined) return num(v);
    return 0;
}
/** requires_level for the source/level badge. Flat entry (legacy + finalized) carries it
 *  at the top level; a full-nested entry (entry IS the ability item_arr, e.g. un-finalized
 *  PC/creature buckets) exposes it only as a LAYER value — mirrors
 *  ft_normalize_sheet_ability_entry()'s layer_value('requires_level') read so the badge
 *  isn't blank. (?? keeps a literal 0 — a level-0 ability still shows "0".) */
export function abilityEntryRequiresLevel(entry: unknown): number | string {
    // Wrapper entries carry requires_level at the top level regardless of whether item_arr is a
    // full nest, a shell, or a bare-UUID ref (v21) — any truthy item_arr marks a wrapper.
    // v23 #5: a renamed wrapper ({item_id: uuid, no flat item_name}) is a wrapper too.
    const entryRecord = recordOf(entry);
    if (entryRecord
        && (entryRecord.item_arr || (typeof entryRecord.item_id === 'string' && entryRecord.item_name === undefined))) {
        return scalarCharge(entryRecord.requires_level ?? '', '');
    }
    const v = ftLayerValue(abilityNest(entry), 'requires_level');
    return (v === null || v === undefined) ? '' : (v as number | string);
}
/** Sort key: the HIGHEST applicable requirement gate. requires_class_level (the "be this class
 *  level" gate, e.g. 37) else requires_level (else 1), MAXed with the highest
 *  requires_skill_tier_N tier on the nest.
 *
 *  SKILL-TIER FOLD (Fable 2026-07-01 — the "openItemInfo sort tier" REVISIT): abilities gated
 *  ONLY by a skill tier (Titan's Bane's Bloody Fury tier 3 / Blood Drinker tier 7 — no
 *  requires_level at all) used to key to the default 1 for BOTH → stable sort → authoring order,
 *  while item.php (sort_items_by_requirements, which folds skill_tier_level in) showed them
 *  tier-sorted. Folding the tier here makes every consumer of this sort (ability-section /
 *  passive-abilities / weapon-abilities panes) match item.php. INVARIANT: the tier is read via
 *  requiredSkillTierEntries — the SAME helper the per-row tier badges use — so wherever badges
 *  render, the sort now sees the same data ("badges show but order wrong" can't recur on this
 *  path). Entries with no tier layers get tierMax 0 → identical keys to before → stable sort
 *  keeps their order unchanged (no regression for untier'd creatures/PCs). */
export function abilityRequiredLevelSort(entry: unknown): number {
    const nest = abilityNest(entry);
    let tierMax = 0;
    for (const { tier } of requiredSkillTierEntries(nest)) {
        if (tier > tierMax) tierMax = tier;
    }
    const cls = ftLayerValue(nest, 'requires_class_level');
    if (cls !== null && cls !== undefined && cls !== '') return Math.max(num(cls, 1), tierMax);
    const lvl = abilityEntryRequiresLevel(entry);
    const lvlN = (lvl === '' || lvl === null || lvl === undefined) ? 1 : num(lvl, 1);
    return Math.max(lvlN, tierMax);
}
export function passiveActive(entry: unknown): boolean {
    // v25 mirror (passive_ability_entry_is_active.php): a bare-UUID catalog ref carries no
    // per-entry compile stamp — same "key absent → active" default as an unstamped object.
    if (typeof entry === 'string') return entry !== '';
    const entryRecord = recordOf(entry);
    if (!entryRecord) return false;
    if (!('passive_conditions_active' in entryRecord)) return true;
    return !!entryRecord.passive_conditions_active;
}

export function uuidOf(e: unknown): string {
    if (typeof e === 'string') return e;
    const record = recordOf(e);
    if (record) return String(ftItemsCol(e, 'item_id') ?? record.item_id ?? '');
    return '';
}

/** Every requires_skill_tier_N entry on an ability nest, with its tier. PREFIX-scanned over
 *  the item_layer_types keys like PHP check_skill_requirements — tiers are NOT bounded to
 *  1..6 (the Lich's Advanced Armor Training gates on Natural Armor Skill tier 7, which a
 *  fixed numeric loop silently missed). */
export function requiredSkillTierEntries(nest: unknown): { tier: number; entry: unknown }[] {
    const lt = recordOf(recordOf(nest)?.item_layer_types);
    if (!lt) return [];
    const out: { tier: number; entry: unknown }[] = [];
    for (const key of Object.keys(lt)) {
        if (!key.startsWith('requires_skill_tier_')) continue;
        const tier = parseInt(key.slice('requires_skill_tier_'.length), 10);
        for (const e of ftLayerEntries(nest, key)) out.push({ tier: Number.isFinite(tier) ? tier : 0, entry: e });
    }
    return out;
}

export const ACTIONABLE_ABILITY_TYPES: readonly string[] = [
    'full_ability', 'ability', 'quick_ability', 'free_ability', 'reaction_ability',
    'attack_ability', 'opportunity_attack_ability', 'curse', 'menta', 'sanguine',
    'charged_ability',
];

export interface AbilitySkillSource {
    id: string;
    name: string;
    type: string;
}

function isSkillSourceType(type: string): boolean {
    const normalized = String(type ?? '').trim().toLowerCase();
    return normalized === 'skill' || normalized.endsWith('_skill');
}

function syntheticSkillName(value: string): string {
    return value
        .replace(/_skill$/i, '')
        .split('_')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ') || 'Skill';
}

/**
 * Resolve the granting skill for a compiled ability entry. Creature blobs expose this in several
 * equivalent shapes: source/built_from UUIDs on rich rows, requires_skill_tier_N on the definition,
 * or (for the leanest static creature rows) membership in computed.creature.skill_ability.
 * Returns null for host-authored and creature-taxonomy/carrier abilities.
 */
export function abilitySkillSource(caster: CachedCaster, entry: unknown): AbilitySkillSource | null {
    const entryRecord = recordOf(entry);
    const candidates = [entryRecord?.source, entryRecord?.built_from];
    for (const candidate of candidates) {
        const id = uuidOf(candidate);
        if (id !== '') {
            const identity = sourceIdentity(caster, id);
            if (isSkillSourceType(identity.type)) {
                return { id, name: identity.name || syntheticSkillName(identity.type), type: identity.type };
            }
        } else if (typeof candidate === 'string' && isSkillSourceType(candidate)) {
            return { id: candidate, name: syntheticSkillName(candidate), type: candidate };
        }
    }

    const nest = resolveAbilityNest(entry, caster);
    for (const requirement of requiredSkillTierEntries(nest)) {
        const id = uuidOf(requirement.entry);
        if (id === '') continue;
        const identity = sourceIdentity(caster, id);
        return { id, name: identity.name || 'Skill', type: identity.type || 'skill' };
    }

    const abilityId = abilityEntryId(entry).toUpperCase();
    const skillBuckets = recordOf(caster.computed?.creature?.skill_ability);
    if (abilityId !== '' && skillBuckets) {
        for (const bucket of Object.values(skillBuckets)) {
            const entries = Array.isArray(bucket) ? bucket : [bucket];
            if (entries.some((candidate) => abilityEntryId(candidate).toUpperCase() === abilityId)) {
                return { id: 'skill', name: 'Skill', type: 'skill' };
            }
        }
    }
    return null;
}

// ── level-requirement gate (TS mirror of PHP meet_ability_requirements) ──
// The ability read funnel (abilityEntriesOfType) merges the gated compiled set
// (computed.creature.abilities) WITH the carrier-walked provider nests
// (ftLayerEntries → _ft_default_provider_carriers walks player_characters.class_1..20 /
// ancestry_* / lineage / background — each a FULL ungated class catalog). That walk
// yields abilities the caster has not unlocked (e.g. a requires_class_level-37 Priest
// ability on a class-23 PC). Gate them here so the funnel only emits abilities whose
// requirements are met — requires_level vs the caster's total_level, and
// requires_class_level vs the caster's level in THAT specific class.

/** UUID → class level, from player_characters.class_1..20 / subclass_1..20. [] for non-PCs. */
function casterClassLevelMap(caster: CachedCaster): Record<string, number> {
    const pc = caster?.player_characters;
    if (!pc || typeof pc !== 'object') return {};
    const map: Record<string, number> = {};
    for (let x = 1; x <= 20; x++) {
        const cls = pc['class_' + x];
        if (cls === undefined || cls === null || cls === '') continue;
        const lvl = num(pc['class_level_' + x], 0);
        const clsId = uuidOf(cls);
        if (clsId) map[clsId.toLowerCase()] = lvl;
        // Subclass shares its parent class slot's level.
        const sub = pc['subclass_' + x];
        const subId = (sub !== undefined && sub !== null && sub !== '') ? uuidOf(sub) : '';
        if (subId) map[subId.toLowerCase()] = lvl;
    }
    return map;
}

/** True when the entry was DELIVERED to the caster by a status / aura (owner 2026-10-07) — PHP twin
 *  ft_ability_entry_is_status_delivered(). Its requires_* gate belongs to the aura on its OWNER, never the
 *  holder: a class aura's payload authored with requires_class_level vanished for every ally. Keyed on the
 *  DELIVERY PATH, never the item (the same passive from a class / feat list carries that grantor as source):
 *  built_from_item_type 'status_effect', else — the leaned blob drops that marker — source = one of the
 *  caster's own status instance ids (a fresh guid per instance, never a grantor item id). */
export function abilityEntryIsStatusDelivered(entry: unknown, caster: CachedCaster): boolean {
    const rec = recordOf(entry);
    if (!rec) return false;
    if (rec.built_from_item_type === 'status_effect') return true;
    const source = typeof rec.source === 'string' ? rec.source.toLowerCase() : '';
    if (source === '') return false;
    const target = recordOf(caster?.statuses)?.target;
    const rows = Array.isArray(target) ? target : (target !== null && target !== undefined ? [target] : []);
    return rows.some((row) => {
        const statusId = recordOf(row)?.status_id;
        return typeof statusId === 'string' && statusId !== '' && statusId.toLowerCase() === source;
    });
}

/** True when the caster meets the entry's level requirements. Reads requires_level and
 *  requires_class_level SEPARATELY (never collapsed) so a low requires_level can't mask a
 *  high requires_class_level. */
export function meetsAbilityRequirements(entry: unknown, caster: CachedCaster, classMap: Record<string, number>): boolean {
    if (entry === null || entry === undefined) return false;

    // Owner carve-out — level/class-level requirements gate PLAYER CHARACTERS only (parity with PHP
    // meet_ability_requirements + ft_gate_carrier_contributed_ability_rows' "PC hosts only" scope).
    // A creature/NPC gets its class mechanics by EXPLICIT authoring (the Summoner's Ether pool pinned
    // to an Eidolon, a class ability granted to a monster), not the ungated PC carrier walk — meant
    // to be there regardless of the creature's own level — so never drop them for a missed
    // requires_level / requires_class_level. (Skill-tier + if_* condition gates still apply; those
    // grey a passive rather than hide it.)
    const isPlayerCharacter = !!caster?.player_characters && typeof caster.player_characters === 'object';
    if (!isPlayerCharacter) return true;
    // A status / aura PAYLOAD is gated on the aura's owner, not re-checked against this holder.
    if (abilityEntryIsStatusDelivered(entry, caster)) return true;

    const nest = abilityNest(entry);
    const totalLevel = num(caster?.computed?.creature?.meta?.total_level ?? ftLayerValue(caster, 'total_level'), 0);

    // requires_level → total-level gate.
    const rl = ftLayerValue(nest, 'requires_level');
    if (rl !== null && rl !== undefined && rl !== '' && num(rl, 0) > totalLevel) return false;

    // requires_class_level → per-class-level gate. The class is named by the ability's
    // requires_class layer (fall back to the entry's source class id). A PC's ability funnel merges
    // the FULL ungated catalogs of the classes it HAS (the carrier walk), so a requires_class the PC
    // lacks means a leaked over-class ability → drop it (classLevel 0). (Creatures never reach here —
    // handled by the carve-out above.)
    const rcl = ftLayerValue(nest, 'requires_class_level');
    if (rcl !== null && rcl !== undefined && rcl !== '') {
        const requiredClassId = String(ftLayerValue(nest, 'requires_class') ?? '').toLowerCase();
        const source = recordOf(entry)?.source;
        const sourceId = typeof source === 'string' ? source.toLowerCase() : '';
        let classLevel: number;
        if (requiredClassId) {
            classLevel = classMap[requiredClassId] ?? 0;   // named class the caster lacks → 0 (drop)
        } else if (sourceId && classMap[sourceId] !== undefined) {
            classLevel = classMap[sourceId] ?? 0;          // source IS one of the caster's class slots
        } else {
            // Source is not a class (an imbued weapon, a status, a feat, an item) or unknown → total
            // level, exactly PHP meet_ability_requirements. Treating a non-class source as "a class the
            // caster lacks" hid Frostwind Blade's Chilling Attack (requires_class_level 5, sourced to
            // the imbued weapon) from the action bar and Abilities tab while the server kept it.
            classLevel = totalLevel;
        }
        if (num(rcl, 0) > classLevel) return false;
    }
    return true;
}

// Universal auto-fire trigger abilities seeded onto EVERY encounter creature in
// finalize_creature_arr.php (default-ability block) so janus_trigger and
// build_caster_roll_type_modifiers can fire them. They already have dedicated UI buttons with
// auto-fire toggles — Resolve in the Core Abilities banner, Evade in the Movement panel
// (render_simple_trigger_ability_button) — so they must NOT also render in the generic
// reaction_ability section, or every creature would show them twice. (do_not_show_ability is
// stripped by build_ability_array, so we exclude by stable ability_id here instead.)
const HIDDEN_UNIVERSAL_ABILITY_IDS: Record<string, true> = {
    'B2BC3219-C745-7050-51DD-892951F0026B': true, // Resolve
    'BE4CCA95-33F0-323D-BA54-844D2540C0F0': true, // Evade
};

/**
 * All ability entries of a type for the caster: the FINALIZED compiled list
 * (computed.creature.abilities.<type>) MERGED with the carrier-walked item_layer_types
 * (ftLayerEntries), deduped by ability id.
 *
 * Why both: the JS ftLayerEntries reads ONLY raw item_layer_types + default-provider carriers,
 * NOT the computed.creature.* clusters ([[feedback_ftlayervalue_no_computed_bridge]]). So
 * compiled-only abilities — e.g. attack abilities (Knockout Strike) and some full abilities —
 * that live solely in computed.creature.abilities were silently dropped, while carrier-granted
 * abilities live only in the walk. PHP layer_entries() sees both; this reader mirrors that.
 * Compiled list first so its richer full-nested shape wins on dedupe.
 */
export function abilityEntriesOfType(caster: CachedCaster, type: string): unknown[] {
    const out: unknown[] = [];
    const seen: Record<string, true> = {};
    const push = (list: unknown): void => {
        const arr = Array.isArray(list) ? list : (list !== null && list !== undefined && list !== '' ? [list] : []);
        for (const e of arr) {
            const idk = abilityEntryId(e);
            if (idk && seen[idk]) continue;
            if (idk) seen[idk] = true;
            out.push(e);
        }
    };
    const compiled = caster.computed?.creature?.abilities?.[type];
    push(compiled);
    // Creature-compile blobs (query_full_item_arr / EC path) name this bucket skill_ability,
    // not abilities — and post-catalogization their item_layer_types ability rows are gone,
    // so without this read a creature statblock's ability/passive tables render EMPTY (Adult
    // Black Dragon witness in tools/test/passive-tree-smoke.mjs). Dual-read both names; the
    // abilityEntryId dedupe above makes overlap harmless if the buckets ever converge.
    push(ftLayerEntries(caster, type));
    // Static item-creature compiles keep natural-weapon/skill-derived rows only in
    // skill_ability. Live encounter compiles now also materialize them as standard ability rows
    // with charges.current; let that richer ftLayerEntries row win dedupe, then use the derived
    // ref only as the static-card fallback.
    push(caster.computed?.creature?.skill_ability?.[type]);
    // Drop entries the caster hasn't unlocked by level — the carrier walk above yields the
    // FULL ungated class/ancestry catalogs, so without this an over-level ability (e.g. a
    // requires_class_level-37 Priest ability on a class-23 PC) would render. Gate once here so
    // every consumer (Abilities tab, passive tree, weapons tables) is level-correct.
    const classMap = casterClassLevelMap(caster);
    // Drop OVERRIDDEN tiers (override_abilities() → computed.creature.suppressed_ability_ids,
    // e.g. Living Weapon d4…d10 under a held d12 — PHP layer_entries suppresses at read time).
    // ftLayerEntries now suppresses these itself, but the direct computed.creature.abilities
    // push above bypasses it — filter the merged list here too.
    const suppressed = caster.computed?.creature?.suppressed_ability_ids;
    const notSuppressed = (e: unknown): boolean => {
        if (!suppressed || typeof suppressed !== 'object') return true;
        const eRecord = recordOf(e);
        const aid = (typeof eRecord?.ability_id === 'string' && eRecord.ability_id !== '')
            ? eRecord.ability_id
            : abilityEntryId(e);
        return !(aid !== '' && (suppressed as Record<string, unknown>)[aid]);
    };
    const notHiddenUniversal = (e: unknown): boolean => {
        const aid = abilityEntryId(e);
        return !(aid !== '' && HIDDEN_UNIVERSAL_ABILITY_IDS[aid.toUpperCase()] === true);
    };
    return out.filter((e) => notSuppressed(e) && notHiddenUniversal(e) && meetsAbilityRequirements(e, caster, classMap));
}

// ── small html helpers ──
export function chargeInput(id: string, min: number | string, max: number | string, value: number | string, ro: boolean, changeHandler = ''): string {
    const oc = changeHandler ? ` ${abilityChargeChangeAttrs(changeHandler)}` : '';
    return `<input id="${escapeHtml(id)}" class="form-control form-control-sm ft-sheet-charges-input" type="number" min="${escapeHtml(String(min))}" max="${escapeHtml(String(max))}" value="${escapeHtml(String(value))}"${oc}${ro ? ' disabled' : ''}>`;
}

/** Emit the SAME inert icon placeholder PHP's icon_ph() does, so the shared scanner
 *  window.ftMountIcons() hydrates it through ONE pipeline: ability-TYPE keys (full_ability,
 *  curse, …) become rune type-badges (RuneIcon.typeBadge — rhymes with the big rune sigils on
 *  the buttons), everything else the registry art. The pane mount calls ftMountIcons(). */
export function iconPh(key: string, w = 25, h = 25, pop = true, title?: string): string {
    const popAttr = pop ? '' : ' data-pop="0"';
    const titleAttr = (title !== undefined && title !== null && title !== '') ? ` data-title="${escapeHtml(title)}"` : '';
    return `<i class="ft-icon" data-icon="${escapeHtml(key)}" data-w="${w}" data-h="${h}"${popAttr}${titleAttr} style="display:inline-block;width:${w}px;height:${h}px" aria-hidden="true"></i>`;
}

/** Variable charge pulled from the matching active passive_ability (consumes/add [x]): the shared investment budget. */
function passiveChargeFor(caster: CachedCaster, targetId: unknown): number | string {
    if (targetId === null || targetId === undefined || targetId === '') return '';
    for (const pa of abilityEntriesOfType(caster, 'passive_ability')) {
        if (passiveActive(pa) && abilityEntryId(pa) === targetId) return sheetChargeBudget(caster, pa);
    }
    return '';
}

function variableInputs(caster: CachedCaster, ab: unknown, id: string, ro: boolean): string {
    let html = '';
    // consumes_charges_qty == '[x]' — the name row's flex `gap` separates these from the name.
    if (ftLayerValue(ab, 'consumes_charges_qty') === '[x]' && ftLayerHas(caster, 'passive_ability')) {
        const v = passiveChargeFor(caster, ftLayerValue(ab, 'consumes_ability_charges'));
        html += chargeInput(`input_${id}_variable_charges`, 0, v, v, ro);
    }
    // add_charges_qty == '[x]'
    if (ftLayerValue(ab, 'add_charges_qty') === '[x]' && ftLayerHas(caster, 'passive_ability')) {
        const v = passiveChargeFor(caster, ftLayerValue(ab, 'add_charges_to_passive_ability'));
        html += chargeInput(`input_${id}_variable_charges`, 0, v, v, ro);
    }
    // dice == '[x]' (mana-invested damage dice) — unless the [x] came from passive charges above.
    const diceXFromPassive = ftLayerValue(ab, 'consumes_charges_qty') === '[x]' || ftLayerValue(ab, 'add_charges_qty') === '[x]';
    if (ftLayerValue(ab, 'dice') === '[x]' && !diceXFromPassive) {
        const mana = sheetManaBudget(caster);
        const min = mana >= 1 ? 1 : 0;
        const val = mana >= 1 ? 1 : 0;
        html += `<input id="input_${escapeHtml(id)}_variable_mana_dice" class="form-control form-control-sm ft-sheet-charges-input" type="number" min="${min}" max="${escapeHtml(String(mana))}" value="${val}" title="Mana invested (damage dice)"${ro ? ' disabled' : ''}>`;
    }
    return html;
}

/** summon / shapeshift choice select (user_choice_1_summon | user_choice_1_shapeshift). */
function choiceSelect(ab: unknown, id: string, layer: string, ro: boolean): string {
    if (!ftLayerHas(ab, layer)) return '';
    const selId = `input_${id}_${layer}`;
    // The EMPTY option's text is the placeholder: Tom Select harvests it for a <select> and
    // overwrites whatever tom-select-init passes, so naming it here is the only thing that sticks.
    const label = userChoicePickerLabel(layer);
    let opts = `<option value="">${escapeHtml(label)}</option>`;
    for (const e of ftLayerEntries(ab, layer)) {
        const uid = uuidOf(e);
        if (!uid) continue;
        // get_item_id_name twin: nested name first; shapeshift/summon entries ship as bare-UUID
        // refs (no nested item_name), so fall back to the referenced-names map before degrading to
        // the raw UUID — else the dropdown shows UUIDs (mirrors action-bar.ts abChoiceSelect).
        let nm = String(ftItemsCol(e, 'item_name') ?? '');
        if (nm === '' && typeof ftItemNameById === 'function') nm = String(ftItemNameById(uid) ?? '');
        if (nm === '') nm = uid;
        nm = decodeHtmlEntitiesForPlainText(nm); // encoded in the DB; escapeHtml below is the encoder
        opts += `<option value="${escapeHtml(uid)}">${escapeHtml(nm)}</option>`;
    }
    // NOT gated on `ro` — the only handler is open-info-value (openItemInfoValue), which opens the
    // side-info panel on the pick: a READ, not an action. Disabling it left a catalog creature page
    // rendering the picker `disabled locked`, so nobody could look at the creature a summon raises.
    // The value is read at cast time too, but a viewer who may not cast is stopped by the cast
    // button and re-checked in use_ability_pre_conditions, so leaving it selectable grants nothing.
    // ts-auto-width goes on the WRAPPER: the css/src rules are descendant selectors
    // (`.ts-auto-width .ts-wrapper`) and Tom Select does not copy the class onto its own wrapper.
    // (`pl-3` was a Bootstrap 4 class this BS5 codebase no longer defines — no padding, and it
    // forced the control onto its own full-width block line.)
    return `<div class="ts-auto-width mt-1"><select class="ts-select ts-auto-width" id="${escapeHtml(selId)}"`
        + ` placeholder="${escapeHtml(label)}" title="${escapeHtml('Choose ' + label)}"`
        + ` ${abilityOpenValueAttrs(selId)}>${opts}</select></div>`;
}

/** status_effect_applied_is_caster_choice (Psion Override Emotion, 2026-10-07): the pick-one status select, under the id the cast button
 *  reads (input_<id>_user_choice_1_status_effect → use_ability_variable_1). Options = the multi-entry applier lists' entries; names from the
 *  nested entry, else the referenced-names map, else the icon registry tooltip. An empty pick = the first entry (server default). Shared
 *  with tabs/mentas.ts and the action bar. */
export function statusChoiceSelect(ab: unknown, id: string, choices?: Array<{ id: string; name: string }> | null): string {
    const options = choices ?? statusChoiceOptions(ab);
    if (!options.length) return '';
    const selId = `input_${id}_user_choice_1_status_effect`;
    const label = userChoicePickerLabel(selId);
    let opts = `<option value="">${escapeHtml(label)}</option>`;
    for (const o of options) opts += `<option value="${escapeHtml(o.id)}">${escapeHtml(o.name)}</option>`;
    return `<div class="ts-auto-width mt-1"><select class="ts-select ts-auto-width" id="${escapeHtml(selId)}"`
        + ` placeholder="${escapeHtml(label)}" title="${escapeHtml('Choose ' + label)}"`
        + ` ${abilityOpenValueAttrs(selId)}>${opts}</select></div>`;
}
/** {id, name} per pick-one status option of `ab` ([] when it carries no caster choice). */
export function statusChoiceOptions(ab: unknown): Array<{ id: string; name: string }> {
    const ids = casterStatusChoiceIds(statusChoiceReaders, ab as Record<string, unknown>);
    if (!ids.length) return [];
    const nested = new Map<string, string>();
    for (const layer of CHOICE_APPLIER_LAYERS) for (const e of ftLayerEntries(ab, layer) ?? []) {
        const uid = choiceEntryUuid(e);
        const nm = String(ftItemsCol(e, 'item_name') ?? '');
        if (uid && nm && !nested.has(uid)) nested.set(uid, nm);
    }
    return ids.map((uid) => {
        let nm = nested.get(uid) ?? '';
        if (nm === '' && typeof ftItemNameById === 'function') nm = String(ftItemNameById(uid) ?? '');
        if (nm === '') nm = String((window as unknown as { FT_ICON_REGISTRY?: Record<string, { tooltip?: string }> }).FT_ICON_REGISTRY?.[uid]?.tooltip ?? '');
        if (nm === '') nm = uid;
        return { id: uid, name: decodeHtmlEntitiesForPlainText(nm) };
    });
}

/** Janus lane PC1 (R-151, coordinator Round 36 P2): an ability_choice BRANCH arrives as PHP-rendered markup from
 *  the browser branch renderer (template.js handleAbilityChoiceChange), which owns the status-choice layer —
 *  so a status-choice branch (Override Emotion) came back as a plain cast button and always cast the first option. This draws
 *  the TS pick-one select into the row's button slot and points the plain cast button at it (useAbilityInput1 → the lane's
 *  statusChoice). The plain buttons are disabled until the branch definition is read, so no cast can slip out unpicked.
 *  A button that already carries its own variable (an at-will / innate marker, a throw …) keeps it — parity with the old PHP
 *  ordering, where the marker won over the status input. */
export async function hydrateStatusChoiceBranch(tr: HTMLElement, childId: string, selectEl?: HTMLSelectElement | null): Promise<void> {
    const id = String(childId ?? '').trim().toUpperCase();
    const slot = tr.querySelector<HTMLElement>('[data-ac-slot="button"]');
    if (!slot || !/^[0-9A-F-]{36}$/.test(id)) return;
    const plainCall = (el: Element): boolean => /handleAbilityBtnSingleClick\(\s*'([0-9A-Fa-f-]{36})'\s*\)/.exec(el.getAttribute('onclick') ?? '')?.[1]?.toUpperCase() === id;
    const plain = Array.from(slot.querySelectorAll<HTMLElement>('[onclick]')).filter(plainCall);
    if (!plain.length) return;
    const held = plain.map(el => ({ el, was: (el as HTMLButtonElement).disabled, pe: el.style.pointerEvents }));
    for (const { el } of held) { (el as HTMLButtonElement).disabled = true; el.style.pointerEvents = 'none'; el.setAttribute('aria-busy', 'true'); }
    try {
        const fetcher = (window as unknown as { fetchAndCacheObject?: (objectId: string) => Promise<unknown> }).fetchAndCacheObject;
        const def = typeof fetcher === 'function' ? await fetcher(id).catch(() => null) : null;
        if (selectEl && (selectEl.value || '').trim().toUpperCase() !== id) return; // a newer pick owns the row
        const options = def ? statusChoiceOptions(def) : [];
        if (!options.length) return;
        const selId = `input_${id}_user_choice_1_status_effect`;
        if (!document.getElementById(selId)) slot.insertAdjacentHTML('afterbegin', statusChoiceSelect(def, id, options));
        for (const { el } of held) el.setAttribute('onclick', `handleuseAbilityInput1BtnSingleClick('${id}', '${selId}');`);
    } finally {
        for (const { el, was, pe } of held) { if (!document.contains(el)) continue; (el as HTMLButtonElement).disabled = was; el.style.pointerEvents = pe; el.removeAttribute('aria-busy'); }
    }
}
(window as unknown as { ftHydrateStatusChoiceBranch?: typeof hydrateStatusChoiceBranch }).ftHydrateStatusChoiceBranch = hydrateStatusChoiceBranch;

/** "user_choice_1_summon" (layer) or "input_<id>_user_choice_1_summon" (select id) → "Summon".
 *  ONE implementation shared by ability-row / castable-spells / mentas so the three pickers cannot
 *  drift apart (castable-spells used to say "Transform" where the others said "Shapeshift").
 *  Falls back to "Choose" rather than emitting a blank placeholder for an unrecognised key. */
export function userChoicePickerLabel(layerOrSelectId: string): string {
    const m = /user_choice_\d+_(.+)$/.exec(String(layerOrSelectId ?? ''));
    const word = (m?.[1] ?? '').replace(/_/g, ' ').trim();
    if (word === '') return 'Choose';
    return word.replace(/\b\w/g, (c) => c.toUpperCase());
}

// Blob-wide index for the source column, built in ONE deep walk and cached per caster object (a WS
// repaint hands us a fresh blob → fresh walk, so this runs once per render, not once per row):
//   • types  — UUID → {item_type, item_name}; the client twin of PHP get_item_id_type() /
//               get_item_id_name(), turning a source UUID into the icon for its TYPE
//               (ancestry / defensive_skill / class / player_character). ALSO keyed by
//               inventory ROW uuid (inventory.inventory_id): inventory/enchantment-granted
//               abilities compile with the ROW uuid as their source (not an item uuid), which
//               used to miss here and mislabel equipment-granted passives as "Host". Those
//               entries carry inv:true + iconId = the nested item (whose registry art / video
//               the icon tiers can use).
//   • videos — UUID → primary video_url, keyed by each item video's video_attached_id, so an item
//               with no flat icon (ancestries are authored as videos, not icons) can fall back to a
//               freeze-frame of its first frame.
// First writer wins for types; a video_primary==1 entry overrides an earlier non-primary one.
export interface BlobSourceIndex {
    types: Record<string, { type: string; name?: string; iconId?: string; inv?: true }>;
    videos: Record<string, string>;
}
const _blobSourceIndexCache = new WeakMap<object, BlobSourceIndex>();
export function blobSourceIndex(caster: CachedCaster | undefined): BlobSourceIndex {
    if (!caster || typeof caster !== 'object') return { types: {}, videos: {} };
    const cached = _blobSourceIndexCache.get(caster);
    if (cached) return cached;
    const types: Record<string, { type: string; name?: string; iconId?: string; inv?: true }> = {};
    const videos: Record<string, string> = {};
    const seen = new Set<object>();
    const walk = (o: unknown): void => {
        if (!o || typeof o !== 'object' || seen.has(o)) return;
        seen.add(o);
        if (Array.isArray(o)) { for (const v of o) walk(v); return; }
        const oRecord = recordOf(o);
        if (!oRecord) return;
        const id = oRecord.item_id, ty = oRecord.item_type;
        if (typeof id === 'string' && id !== '' && typeof ty === 'string' && ty !== '') {
            const k = id.toLowerCase();
            if (!types[k]) types[k] = { type: ty, name: typeof oRecord.item_name === 'string' ? oRecord.item_name : undefined };
        }
        const invRow = recordOf(oRecord.inventory);
        if (invRow && typeof invRow.inventory_id === 'string' && invRow.inventory_id !== '') {
            const ik = invRow.inventory_id.toLowerCase();
            if (!types[ik]) {
                const nested = recordOf(oRecord.items);
                const invName = (typeof invRow.inventory_name === 'string' && invRow.inventory_name !== '') ? invRow.inventory_name : undefined;
                types[ik] = {
                    type: (nested && typeof nested.item_type === 'string' && nested.item_type !== '') ? nested.item_type : 'inventory',
                    name: invName ?? ((nested && typeof nested.item_name === 'string') ? nested.item_name : undefined),
                    iconId: (nested && typeof nested.item_id === 'string' && nested.item_id !== '') ? nested.item_id : undefined,
                    inv: true,
                };
            }
        }
        const vids = oRecord.item_videos;
        if (Array.isArray(vids)) {
            for (const v of vids) {
                if (!v || typeof v !== 'object') continue;
                const video = recordOf(v);
                if (!video) continue;
                const url = typeof video.video_url === 'string' ? video.video_url : '';
                const aid = video.video_attached_id ? String(video.video_attached_id).toLowerCase() : '';
                if (!url || !aid) continue;
                if (Number(video.video_primary) === 1 || !(aid in videos)) videos[aid] = url;
            }
        }
        for (const child of Object.values(oRecord)) walk(child);
    };
    walk(caster);
    const idx: BlobSourceIndex = { types, videos };
    _blobSourceIndexCache.set(caster, idx);
    return idx;
}

// Default-provider carrier layers (taxonomy + classifications) that nest abilities onto a creature —
// mirror of FT_DEFAULT_PROVIDER_LAYERS (item-arr-readers.js), the same set ftLayerEntries walks to
// surface a creature's abilities. The ability buckets a carrier can grant.
const _PROVIDER_LAYERS = ['creature_template', 'creature_sub_type', 'creature_type', 'weapon_classification', 'weapon_ammo_classification', 'armor_classification', 'instrument_classification', 'armor_type'];
const _ABILITY_BUCKETS = ['full_ability', 'ability', 'quick_ability', 'free_ability', 'reaction_ability', 'passive_ability', 'attack_ability', 'opportunity_attack_ability', 'ability_choice', 'curse', 'sanguine', 'charged_ability'];
const _abilitySourceMapCache = new WeakMap<object, Record<string, string>>();

/** Map an ability's UUID → the UUID of the provider carrier (creature_sub_type / classification / …)
 *  that nests it, so the source column can show e.g. Aggressive → the Orcs creature_sub_type. Built by
 *  walking the same default-provider carriers ftLayerEntries does; the OUTERMOST carrier wins. Abilities
 *  authored directly on the host (no carrier) aren't in the map — those rely on requires_skill_tier /
 *  entry.source instead. Memoized per caster (WeakMap, like blobSourceIndex). Exported for the passive
 *  tree's creature-aware grouping (a creature's source:'NA' passives group under their carrier). */
export function abilitySourceMap(caster: CachedCaster): Record<string, string> {
    if (!caster || typeof caster !== 'object') return {};
    const cached = _abilitySourceMapCache.get(caster);
    if (cached) return cached;
    const map: Record<string, string> = {};
    const resolveCarrier = (entry: unknown): UnknownRecord | null => {
        if (entry === null || entry === undefined || entry === '') return null;
        const entryRecord = recordOf(entry);
        if (entryRecord) {
            const ilt = recordOf(entryRecord.item_layer_types);
            if (ilt && Object.keys(ilt).length > 0) return entryRecord;
        }
        const id = typeof entry === 'string' ? entry : uuidOf(entry);
        if (id !== '') {
            const catalog = caster.catalog;
            const fromCatalog = catalog
                ? catalog[id] ?? catalog[id.toUpperCase()] ?? catalog[id.toLowerCase()]
                : null;
            if (fromCatalog) return fromCatalog;
            const cachedCarrier = findFullNestedItem(id);
            if (cachedCarrier) return cachedCarrier;
        }
        return entryRecord;
    };
    const carriersOf = (node: unknown): UnknownRecord[] => {
        const out: UnknownRecord[] = [];
        const lt = recordOf(recordOf(node)?.item_layer_types);
        if (!lt) return out;
        for (const pl of _PROVIDER_LAYERS) {
            const slot = lt[pl];
            if (slot === null || slot === undefined || slot === '') continue;
            const entries = Array.isArray(slot) ? slot : [slot];
            for (const e of entries) {
                const carrier = resolveCarrier(e);
                if (carrier) out.push(carrier);
            }
        }
        return out;
    };
    const record = (carrier: UnknownRecord, sourceId: string): void => {
        const lt = recordOf(carrier.item_layer_types);
        if (lt) {
            for (const at of _ABILITY_BUCKETS) {
                const slot = lt[at];
                if (!slot || typeof slot !== 'object') continue;
                const entries = Array.isArray(slot) ? slot : [slot];
                for (const e of entries) {
                    const id = String(abilityEntryId(e) ?? '').toLowerCase();
                    if (id && !map[id]) map[id] = sourceId;
                }
            }
        }
        for (const nested of carriersOf(carrier)) record(nested, sourceId); // keep the outermost source
    };
    for (const carrier of carriersOf(caster)) {
        const sid = String(uuidOf(carrier) ?? '');
        if (sid) record(carrier, sid);
    }
    _abilitySourceMapCache.set(caster, map);
    return map;
}

// Blob-wide index: ability item_id → its FULL nested item_arr (a node carrying item_layer_types).
// The global dedup ([[project_full_nest_global_dedup]]) keeps ONE full copy of each ability/spell
// and SHELLS the rest ({items:{item_id}} only). With dedup enabled on encounter creatures, a
// carrier-granted entry (e.g. an Undead creature_type passive) can reach a pane as a shell — this
// re-resolves it to its full in-blob copy at render so buildRow doesn't skip it. Pure client, no
// re-query; cached per caster object (a WS repaint hands a fresh blob → fresh walk). Mirrors the
// list-display rune shell-resolve ([[reference_rune_shell_reresolve]]). First full copy wins.
// fullAbilityById WAS a local abilities-only blob index; Phase 5 stage 2
// generalized the byte-identical walk into the shared `fullNodeById`
// (array-types.ts) so spells, natural weapons, classifications, and creatures
// resolve their full in-blob copy the same way once stage 3 collapses duplicate
// nests. Kept as a thin alias — behavior is unchanged (same first-FULL-copy-wins,
// WeakMap-cached index keyed by lowercased item_id).
const fullAbilityById = fullNodeById;

/** Like abilityNest(), but if the entry's own nest is a dedup SHELL (no item_layer_types), resolve
 *  it to the full in-blob copy by id (fullAbilityById). Falls back to the shell when no full copy
 *  exists anywhere (then buildRow's shell guard still applies). */
export function resolveAbilityNest(entry: unknown, caster?: CachedCaster): UnknownRecord | string | null {
    const nest = abilityNest(entry);
    if (recordOf(nest)?.item_layer_types) return nest;
    const id = abilityEntryId(entry);
    if (id && caster) {
        const full = fullAbilityById(caster)[id.toLowerCase()];
        if (full) return full;
    }
    // Global fallback for a BARE-UUID-STRING entry (Stage-3 pure ref — e.g. the Paladin smites
    // Atonement / Penance / Holy Reckoning). abilityNest() resolves DICT refs ({item_id}) via
    // findFullNestedItem but returns a bare string AS-IS, and fullAbilityById(caster) only indexes
    // THIS caster object — so it misses whenever the def lives outside it (most notably an
    // encounter-re-seeded member blob whose own .catalog was unioned up to the encounter root).
    // Without this the row's nest stays a string and buildRow's `!ab.items` guard SKIPS it entirely
    // (the 3 bare-string Paladin attacks rendered nowhere while the 6 dict-ref attacks did). Walk
    // every cache + root catalog the same way the dict path does. [[feedback_ftlayervalue_no_computed_bridge]]
    if (id) {
        const full = findFullNestedItem(id);
        if (full) return full;
    }
    return nest;
}

/** Non-playable freeze-frame of an item's primary video — the first frame, painted via a #t media
 *  fragment (browser seeks + renders that frame without ever autoplaying). The source-column icon
 *  for items we have no flat art for (ancestries). Shows the WHOLE frame at its native aspect (no
 *  square crop) — width responsive via clamp(46px,6.5vw,68px), height auto — so nothing of the
 *  figure is cut off and it grows on desktop while staying compact on tablet/mobile (the source
 *  column uses the matching clamp(50px,7vw,72px)). The OUTER span owns the click + tooltip; an INNER clipped
 *  wrapper holds the <video> so the level pill (absolute, top-right) isn't clipped by the rounded
 *  corners' overflow:hidden. */
export function videoFreezeFrameCell(url: string, infoId: string, title: string, lvlPill: string): string {
    const id = escapeHtml(infoId);
    const t = escapeHtml(title);
    const src = escapeHtml(url + (url.includes('#') ? '' : '#t=0.1'));
    return `<span class="position-relative d-inline-block ft-ability-source-video" role="button"`
        + ` ${abilityInfoAttrs(infoId)} data-ft-info-item-id="${id}"`
        + ` data-bs-toggle="tooltip" data-bs-placement="top" data-bs-original-title="${t}">`
        + `${lvlPill}`
        + `<span class="d-block mx-auto" style="width:clamp(46px,6.5vw,68px);border-radius:4px;overflow:hidden;border:1px solid rgba(255,255,255,.2);box-shadow:0 0 0 1px rgba(0,0,0,.35);line-height:0;">`
        + `<video src="${src}" muted playsinline preload="metadata" tabindex="-1" aria-hidden="true"`
        + ` style="width:100%;height:auto;display:block;pointer-events:none;"></video>`
        + `</span></span>`;
}

/**
 * Source / requirement icon for the source column. Client port of PHP
 * show_requires_class_level_icons() (includes/functions.php) + the source cell in
 * render_passive_abilities_table() / render_abilities_table() (which emit
 * icon_map(get_item_id_type($source))) — the requires-level / requires-class-level gate rides as
 * a bg-success pill ON the icon, so there is NO standalone level column.
 * (See [[feedback_ability_source_level_pill_on_icon]].)
 *
 *   - requires_subclass / requires_class on the ability → that class/subclass art (by UUID) +
 *     the requires_class_level pill (subclass wins).
 *   - source ability (no class req) → a 3-tier fallback: (1) the EXACT granting item's art when it
 *     has a registered icon (Catlike Landing → the Balance skill glyph); (2) else, if the item has
 *     a primary video but no flat icon (ancestries), a full-frame freeze-frame of that video; (3) else
 *     its item-TYPE icon from the blob (get_item_id_type twin: ancestry / defensive_skill /
 *     innate_skill / class / player_character — the genuine host/self case). NOT a hardcoded 'host',
 *     which mislabeled skill- and ancestry-granted passives as "Host". Icons via iconMap, not iconPh.
 *   - no source → a bare level badge so the gate stays visible.
 *
 * The OUTER span owns the click + press-hold (data-ft-info-item-id); the icon itself is rendered
 * with iconMap so it carries the standardized Bootstrap tooltip (data-bs-toggle="tooltip").
 */
export function sourceIconCell(entry: unknown, ab: unknown, caster?: CachedCaster): string {
    const sub = ftLayerEntries(ab, 'requires_subclass')[0];
    const cls = ftLayerEntries(ab, 'requires_class')[0];
    const pick = sub ?? cls;
    if (pick) {
        const uid = uuidOf(pick);
        const itype = String(ftItemsCol(pick, 'item_type') ?? (sub ? 'subclass' : 'class'));
        const nm = String(ftItemsCol(pick, 'item_name') ?? '');
        const lvl = ftLayerValue(ab, 'requires_class_level');
        const pill = (lvl !== null && lvl !== undefined && lvl !== '')
            ? `<span class="position-absolute top-0 start-100 translate-middle badge border rounded-pill bg-success">${escapeHtml(String(lvl))}</span>`
            : '';
        const info = uid ? ` ${abilityInfoAttrs(uid)} data-ft-info-item-id="${escapeHtml(uid)}"` : '';
        const reg = (typeof ICON_LOOKUP !== 'undefined') ? ICON_LOOKUP : undefined;
        const iconKey = (uid && reg && reg[uid]) ? uid : itype;
        // iconMap (direct <img> wrapped in the standardized data-bs-toggle="tooltip" span), not the
        // iconPh placeholder — so the class/subclass icon gets the same Bootstrap tooltip the rest
        // of the sheet uses instead of an aria-hidden glyph with only a raw native-title hover.
        return `<span class="position-relative d-inline-block" role="button"${info}>${pill}${iconMap(iconKey, 25, 25, nm || '')}</span>`;
    }
    const lvl = abilityEntryRequiresLevel(entry);
    const lvlPill = (lvl !== null && lvl !== undefined && lvl !== '')
        ? `<span class="position-absolute top-0 start-100 translate-middle badge border rounded-pill bg-success">${escapeHtml(String(lvl))}</span>`
        : '';
    // Weapon-tied attack abilities (e.g. Cleave) carry no requires_class/subclass but ARE gated on an
    // equipped weapon classification (if_weapon_classification_equipped_active = Battleaxes). Surface
    // that classification as the ability's source so the statblock shows where the attack comes from.
    const wcEntry = ftLayerEntries(ab, 'if_weapon_classification_equipped_active')[0];
    if (wcEntry) {
        const wid = uuidOf(wcEntry);
        const wnm = String(ftItemsCol(wcEntry, 'item_name') ?? '');
        const wInfo = wid ? ` ${abilityInfoAttrs(wid)} data-ft-info-item-id="${escapeHtml(wid)}"` : '';
        const wReg = (typeof ICON_LOOKUP !== 'undefined') ? ICON_LOOKUP : undefined;
        const wIconKey = (wid && wReg && wReg[wid]) ? wid : 'weapon_classification';
        return `<span class="position-relative d-inline-block" role="button"${wInfo}>${lvlPill}${iconMap(wIconKey, 25, 25, wnm || '')}</span>`;
    }
    let source = recordOf(entry)?.source ?? null;
    if (source === 'NA' || source === '') source = null; // creature abilities compile to source:'NA'
    if (!source) {
        // Skill-gated ability → show the gating skill as its source (Steadfast Posture → Medium Armor
        // Skill via requires_skill_tier_1). The skill UUID is right on the ability nest. Prefix-scanned:
        // tiers run past 6 (Natural Armor tier 7 on the Lich).
        for (const { entry: sk } of requiredSkillTierEntries(ab)) {
            const u = uuidOf(sk);
            if (u) { source = u; break; }
        }
    }
    if (!source && caster) {
        // Surfaced from a taxonomy/classification carrier (Aggressive → Orcs creature_sub_type).
        const aid = String(abilityEntryId(entry) ?? '').toLowerCase();
        if (aid) { const cs = abilitySourceMap(caster)[aid]; if (cs) source = cs; }
    }
    // Final fallback: an ability with NO resolvable provenance (no requires_class/subclass, weapon
    // classification, requires_skill_tier, entry.source, or carrier) is authored directly ON the
    // creature/character — its source IS the host. Safe now that all the REAL source resolvers above
    // run first (the host-mislabel regression that removed this only happened because those didn't
    // exist yet), so this only fires for genuinely host-authored abilities — e.g. a creature's own
    // attack abilities (Cinder Claw / Ember Slam / Furnace Bite). 'host' is a synthetic non-UUID key →
    // the branch below renders iconMap('host') with no openItemInfo wrapper.
    if (!source) source = 'host';
    if (source) {
        const srcStr = String(source);
        // Synthetic non-UUID source (e.g. 'host' on the auto-granted Short Rest — the ability rides
        // on the character itself, not a catalog item): render its registered icon directly, with NO
        // openItemInfo wrapper — there is no item page behind it to open.
        if (typeof checkIfValidUUID === 'function' && !checkIfValidUUID(srcStr)) {
            return `<span class="position-relative d-inline-block">${lvlPill}${iconMap(srcStr, 25, 25)}</span>`;
        }
        const blob = blobSourceIndex(caster);
        const info = blob.types[srcStr.toLowerCase()];
        const ident = sourceIdentity(caster, srcStr); // catalog fallback for thinned-out defs
        const titleTxt = ident.name || ident.type || 'Source';
        const reg = (typeof ICON_LOOKUP !== 'undefined') ? ICON_LOOKUP : undefined;
        const outer = (inner: string) =>
            `<span class="position-relative d-inline-block" role="button" ${abilityInfoAttrs(srcStr)} data-ft-info-item-id="${escapeHtml(srcStr)}">${lvlPill}${inner}</span>`;
        // (1) EXACT granting-item art when it has its own registered icon — Balance skill → Balance
        //     glyph, Priest class → its sigil. iconMap (direct <img>), not the iconPh placeholder.
        if (reg && reg[srcStr]) return outer(iconMap(srcStr, 25, 25, titleTxt));
        // (1b) Inventory-ROW source (equipment / enchantment grants compile with the inventory_id
        //      as their source): the row uuid has no registry art of its own, so use the NESTED
        //      item's registered icon, then its primary video.
        if (info?.iconId) {
            if (reg && reg[info.iconId]) return outer(iconMap(info.iconId, 25, 25, titleTxt));
            const invVideo = blob.videos[info.iconId.toLowerCase()];
            if (invVideo) return videoFreezeFrameCell(invVideo, srcStr, titleTxt, lvlPill);
        }
        // (2) No flat icon, but the item has a primary video (ancestries are authored as videos) →
        //     a full-frame, non-playable freeze-frame of its first frame.
        const videoUrl = blob.videos[srcStr.toLowerCase()];
        if (videoUrl) return videoFreezeFrameCell(videoUrl, srcStr, titleTxt, lvlPill);
        // (3) Fall back to the source's item-TYPE icon (ancestry / defensive_skill / jewelry /
        //     weapon / player_character…) — for inventory rows this is the nested item's type,
        //     no longer the hardcoded 'host'.
        return outer(iconMap(ident.type || 'host', 25, 25, titleTxt));
    }
    return lvlPill
        ? `<span role="button" class="badge bg-dark border text-white">${escapeHtml(String(lvl))}</span>`
        : '';
}

/** Charge-COST chip: how many charges of WHICH pool this ability spends (or grants) per use —
 *  Hasted Attack = "1 ×Aether", Elemental Attack = "[x] ×Aether". Leads the CHARGES column (far
 *  right) in the list rows (owner request 2026-07-01) so the cost is visible without opening the
 *  ability. Mirrors the spell tables' specialChargesCell (castable-spells.ts):
 *  qty badge + the pool ability's own art (chargeAbilityIcon — name-keyed CDN icon + tooltip).
 *  Consumes render first (red border), then add_charges grants (green border). '' when neither. */
export function chargeCostChip(ab: unknown): string {
    let out = '';
    const piece = (refLayer: string, qtyLayer: string, cls: string, verb: string): string => {
        const ref = ftLayerEntries(ab, refLayer)[0];
        if (ref === undefined || ref === null) return '';
        const refId = uuidOf(ref);
        if (!refId) return '';
        const qtyRaw = ftLayerValue(ab, qtyLayer);
        const qty = (qtyRaw === null || qtyRaw === undefined || qtyRaw === '') ? '1' : String(qtyRaw);
        return `<span class="d-inline-flex align-items-center gap-1 flex-nowrap ft-ability-cost-chip">`
            + `<span class="badge bg-dark ${cls}" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-original-title="${escapeHtml(`${verb} ${qty} charge(s) per use`)}">${escapeHtml(qty)}</span>`
            + chargeAbilityIcon(refId, 20, 20)
            + `</span>`;
    };
    out += piece('consumes_ability_charges', 'consumes_charges_qty', 'border border-danger', 'Consumes');
    out += piece('add_charges_to_ability', 'add_charges_qty', 'border border-success', 'Adds');
    return out;
}

/** Which OPTIONAL columns a section renders — computed once per section (buildSection) from all
 *  its rows, so every row emits the same cell set and the columns stay consistent down the table:
 *  `effect` = the damage roll unit (dice button + damage-type select), its own RIGHT-aligned column
 *  to the LEFT of the source/requires-class column; `cost` = the charge-cost chip (consumes/adds
 *  × pool icon) just RIGHT of the source column, left of the charges input/recharge column
 *  (owner 2026-07-01 v5). */
export interface AbilityRowCols { effect: boolean; cost: boolean }

/** One ability row (button | name | [effect] | source+level pill | [cost] | charges). */
export function buildRow(caster: CachedCaster, entry: unknown, type: string, canEdit: boolean, cols: AbilityRowCols = { effect: false, cost: false }): string {
    const ab = recordOf(resolveAbilityNest(entry, caster));
    // Skip leaned shells (no item_layer_types) — render nothing rather than a degraded button.
    if (!ab?.item_layer_types) {
        // a bare-id / leaned entry: skip (curse/sanguine/charged may land here). // TODO hydrate.
        if (!ab || !ab.items) return '';
    }
    const ro = !canEdit;
    const id = abilityEntryId(entry);
    let name = abilityEntryName(entry);
    // Bare-UUID creature attack abilities: item_layer_types.attack_ability holds only refs, and the
    // ability's full def lives in THIS blob (catalog or a nested copy) but not necessarily in the
    // global caches / root catalog that abilityEntryName's resolver walks — and that resolver's DFS
    // skips `items` keys (SKIP_KEYS), so a copy reachable only there is missed. resolveAbilityNest
    // (fullNodeById) walks this blob INCLUDING `items`, so `ab` already holds the def + its name (it's
    // what renders the rune + dice). When the entry-level resolver misses, fall back to `ab`'s name.
    // Only fires on a miss, so an intentionally-divergent wrapper name is never overridden. Fixes the
    // blank attack-ability names on item-creature statblocks (e.g. the Eidolon: Adolescent Ifrit's
    // Cinder Claw / Ember Slam / Furnace Bite).
    if (name === '' || name === 'Unknown') {
        // This fallback bypasses abilityEntryName's decode, so decode here too — otherwise the
        // item-creature statblock names it rescues render their entities literally ("&#039;").
        const nestName = (ab && typeof ab === 'object') ? ftItemsCol(ab, 'item_name') : '';
        if (typeof nestName === 'string' && nestName !== '') name = decodeHtmlEntitiesForPlainText(nestName);
    }
    const entryRecord = recordOf(entry);
    const source = entryRecord?.source ?? null;
    const searchName = escapeHtml(name.toLowerCase());

    const isChargedAbility = type === 'charged_ability';
    const spellType = isChargedAbility ? 'charged_ability' : null;
    // consume_nested_unit_per_use is a synthetic flag stamped by build_ability_array() onto the ENTRY
    // (sibling of item_arr/source), NOT into the ability's own item_layer_types — so read it off `entry`,
    // not the resolved nest `ab`. Reading `ab` returned false for every consumable (ammo/potion/coating),
    // so the sheet button never sent nested_inventory_source and the unit was never consumed on use.
    const consumeNested = entryRecord?.consume_nested_unit_per_use === true;

    // ability_choice parent (best-effort: only when options are present in the blob)
    const acOptions = ab?.ability_choice_options;
    const isChoiceParent = Array.isArray(acOptions) && acOptions.length > 0;

    // Roll-damage effect button (port of render_ability_effect_button) — shown on creatures AND
    // PCs. Placed at the RIGHT edge of the NAME cell (ms-auto) so it sits to the LEFT of the
    // source / requires-level column and never overlaps it. ability_choice parents skip it
    // (their effect is filled client-side into the data-ac-slot="effect" in the name cell).
    const effectHtml = isChoiceParent ? '' : abilityEffectButton(caster, ab, id, ro);

    // ── button cell ──
    let buttonCell: string;
    if (isChoiceParent) {
        buttonCell = '<div data-ac-slot="button" class="text-center"></div>';
    } else {
        buttonCell = generateAbilityButton(ab, caster, {
            spellType,
            source: typeof source === 'string' ? source : null,
            consumeNestedUnitPerUse: consumeNested,
            buttonDisabled: !canEdit,
        });
    }

    // ── name cell ──
    let nameCell: string;
    if (isChoiceParent) {
        let opts = '<option value="">-- SELECT OPTION --</option>';
        for (const o of acOptions) {
            const oid = String(o?.item_id ?? '');
            // item_name is server-populated (ft_name_from_blob); on a catalog miss it can be '' —
            // fall back to the referenced-names map, then the id, so an option is never label-less.
            let onm = String(o?.item_name ?? '');
            if (onm === '' && oid !== '' && typeof ftItemNameById === 'function') onm = String(ftItemNameById(oid) ?? '');
            if (onm === '') onm = oid;
            onm = decodeHtmlEntitiesForPlainText(onm); // encoded in the DB; escapeHtml below is the encoder
            opts += `<option value="${escapeHtml(oid)}">${escapeHtml(onm)}</option>`;
        }
        nameCell = `<div class="mb-2"><select class="ts-select form-select form-select-sm" id="input_${escapeHtml(id)}_ability_choice_branch" ${abilityChoiceChangeAttrs()}${ro ? ' disabled' : ''}>${opts}</select></div>`
            + `<div class="d-flex align-items-center flex-wrap gap-2 mb-1"><div data-ac-slot="name" class="small"></div></div>`
            + `<div data-ac-slot="effect" class="small"></div>`
            + `<div class="mt-1 small text-muted"><span role="button" class="link-secondary" ${abilityInfoAttrs(id)}>${escapeHtml(name)}</span></div>`;
    } else {
        // Flex row: any variable charge inputs sit inline before the name, `gap` (CSS) spaces
        // them; the gap from the rune-button column comes from CSS padding on the cell
        // (.ft-ability-cell--name) rather than a per-element utility class. The damage-roll
        // effect unit no longer rides here (ms-auto) — it gets its OWN column (cols.effect) so
        // the dice buttons line up flush left of the source column all the way down; the ms-auto
        // form is kept only for legacy callers that don't opt into the effect column.
        nameCell = `<div class="d-flex align-items-center gap-2 ft-ability-name">${variableInputs(caster, ab, id, ro)}`
            + `<span role="button" class="ft-ability-name__label" ${abilityInfoAttrs(id)}>${escapeHtml(name)}</span>`
            + (!cols.effect && effectHtml ? `<span class="ms-auto flex-shrink-0 ft-ability-effect">${effectHtml}</span>` : '')
            + `</div>`
            + choiceSelect(ab, id, 'user_choice_1_shapeshift', ro)
            + choiceSelect(ab, id, 'user_choice_1_summon', ro)
            + statusChoiceSelect(ab, id);
    }

    // ── source / charges ──
    const sourceCell = sourceIconCell(entry, ab, caster);

    let chargesInner = '';
    const charges = abilityEntryCharges(entry);
    if (charges !== 'NA') {
        const maxC = abilityEntryMaxCharges(entry);
        const rb = abilityEntryRechargeBegin(entry);
        const rechargeBadge = rb > 0
            ? `<h5 class="mb-0"><span class='badge bg-small bg-dark border'>${escapeHtml(String(rb))}</span> ${iconPh('recharge_range_begin')}</h5>`
            : `<h5 class="mb-0"><span class='badge bg-small bg-dark border'>${escapeHtml(String(maxC))}</span> ${iconPh('ability_resets_long_rest')}</h5>`;
        chargesInner = chargeInput(`${id}_charges`, 0, maxC, charges, ro, `changeCharges('${id}','${id}_charges');`)
            + `<div class="flex-shrink-0">${rechargeBadge}</div>`;
    }
    const chargesCell = chargesInner
        ? `<div class="d-flex align-items-center justify-content-end gap-2 ft-sheet-charges-row">${chargesInner}</div>`
        : '';
    // TODO charged_ability inventory-charge inputs (get_inventory_current_item_charges — server-only).

    // data-ft-caster-item-id is REQUIRED: handleAbilityChoiceChange posts it as `caster_id`, and
    // The browser branch renderer resolves its caster with this id — omitting it made every
    // ability-type branch pick fail with "Invalid caster or ability id". An ENCOUNTER CREATURE must
    // be identified by its encounter_creature_id (the endpoint then gates on
    // check_encounter_creature_permission_d_arr), same precedence ft_build_castable_spells_data uses.
    // [[Codex review 2026-07-28 P1]]
    const ecRow = caster?.encounter_creatures;
    const ecId = (ecRow && !Array.isArray(ecRow)) ? ecRow.encounter_creature_id : undefined;
    const unwrapId = (v: unknown): string => String((Array.isArray(v) ? v[0] : v) ?? '');
    const casterReqId = unwrapId(ecId) || String(ftItemsCol(caster, 'item_id') ?? '');
    const choiceAttrs = isChoiceParent
        ? ` data-ft-ability-choice-parent="${escapeHtml(id)}" data-ft-caster-item-id="${escapeHtml(casterReqId)}" data-ft-ac-spell-type="${escapeHtml(spellType ?? '')}" data-ft-ac-source="${escapeHtml(String(source ?? ''))}" data-ft-ac-consume-nested="${consumeNested ? '1' : '0'}"`
        : '';

    // OPTIONAL section columns (see AbilityRowCols), owner 2026-07-01 v5 ordering:
    //   name | DAMAGE (dice button + type select) | source | charge COST | charges/resets
    // — the damage unit now sits to the LEFT of the requires-class/source icon (owner request),
    // RIGHT-aligned in its own column with side padding (.ft-ability-cell--effect CSS) so every
    // dice button lines up flush against the source column all the way down the section. Then to
    // the RIGHT of source: the charge COST chip (charges the mechanic requires), then the
    // once-per-day / recharge charges column.
    const effectCell = cols.effect
        ? `<td class="ft-ability-cell ft-ability-cell--effect align-middle text-end">${isChoiceParent ? '' : effectHtml}</td>`
        : '';
    const costCell = cols.cost
        ? `<td class="ft-ability-cell ft-ability-cell--cost align-middle text-center">${chargeCostChip(ab)}</td>`
        : '';

    return `<tr class="fate-tide-sheet-ability-row${isChoiceParent ? ' fate-tide-ability-choice-row' : ''}" data-ft-ability-name="${searchName}"${choiceAttrs}>
        <td class="ft-ability-cell ft-ability-cell--btn align-middle text-center" style="width:70px;">${buttonCell}</td>
        <td class="ft-ability-cell ft-ability-cell--name align-middle">${nameCell}</td>
        ${effectCell}
        <td class="ft-ability-cell ft-ability-cell--source align-middle text-center px-2" style="width:clamp(50px,7vw,72px);">${sourceCell}</td>
        ${costCell}
        <td class="ft-ability-cell ft-ability-cell--charges align-middle text-end px-2 ft-sheet-charges-cell">${chargesCell}</td>
    </tr>`;
}

export function ucLabel(type: string): string {
    return `${ucwordsAndSpace(type.replace(/ability/g, ''))} Abilities`.replace(/\s+/g, ' ').trim();
}

// Ability-type → colour-themed header class (the SAME ft-ability-hdr / ft-ah--* system the
// list-display section headers use; hues defined in css/src/90-after.css, mirroring TYPE_COLOR
// in includes/js-src/rune-icon/rune-icon-engine.ts). Drives the dark hue-tinted gradient band
// so the caster-sheet ability sections match the item/creature list display.
const AH_HDR_MODIFIER: Record<string, string> = {
    passive_ability: 'passive', full_ability: 'full', ability: 'ability',
    quick_ability: 'quick', attack_ability: 'attack', free_ability: 'free',
    reaction_ability: 'reaction', opportunity_attack_ability: 'opportunity',
    ability_choice: 'choice', curse: 'curse', menta: 'menta',
    sanguine: 'sanguine', charged_ability: 'charged',
};
export function abilityHeaderClass(type: string): string {
    return `ft-ability-hdr ft-ah--${AH_HDR_MODIFIER[type] ?? 'ability'}`;
}

/** True when an entry was granted by an EQUIPPED INVENTORY ITEM (a weapon's attack ability) rather
 *  than a class / host / ancestry — i.e. its `source` resolves to an inventory ROW (inv:true in the
 *  blob source index). Weapon-granted attacks (Chain-Drag Smash / Falling Star Swing / Meteor Path
 *  off the equipped Meteor Hammer) carry no requires_class_level, so on a plain level sort they
 *  interleave with the level-1 CLASS attacks; grouping them last keeps the class progression
 *  (Holy Reckoning → Divine Smite → Penance → Atonement) reading cleanly in class-level order. */
function isInventorySourcedEntry(entry: unknown, caster: CachedCaster): boolean {
    const src = recordOf(entry)?.source;
    if (typeof src !== 'string' || src === '' || src === 'NA') return false;
    return blobSourceIndex(caster).types[src.toLowerCase()]?.inv === true;
}

/** Order an ability section. For the weapon attack tables (attack / opportunity_attack) the rows are
 *  grouped CLASS-granted first then INVENTORY/weapon-granted, each ascending by required class level
 *  (abilityRequiredLevelSort: requires_class_level, else requires_level, else 1). Every other section
 *  keeps the plain required-level order. Array.prototype.sort is stable, so same-key authored order
 *  is preserved within each group. */
export function sortAbilitySection(caster: CachedCaster, type: string, entries: unknown[]): unknown[] {
    const groupWeapons = type === 'attack_ability' || type === 'opportunity_attack_ability';
    return [...entries].sort((a, b) => {
        if (groupWeapons) {
            const ra = isInventorySourcedEntry(a, caster) ? 1 : 0;
            const rb = isInventorySourcedEntry(b, caster) ? 1 : 0;
            if (ra !== rb) return ra - rb;
        }
        return abilityRequiredLevelSort(a) - abilityRequiredLevelSort(b);
    });
}

/** Build one full ability-type section (header + sorted rows table). `opts.headerSuffixHtml`
 *  injects extra header content (e.g. the weapons class-mechanic charge banner). */
export function buildSection(
    caster: CachedCaster,
    type: string,
    color: string,
    canEdit: boolean,
    opts: { headerSuffixHtml?: string } = {},
): string {
    const entries = abilityEntriesOfType(caster, type);
    if (!entries || entries.length === 0) return '';
    // Class-granted abilities first (ascending requires_class_level), then weapon/inventory-granted
    // attacks — see sortAbilitySection. Stable, so same-level authored order is preserved.
    const sorted = sortAbilitySection(caster, type, entries);
    // Section-wide OPTIONAL columns: include the damage-effect / charge-cost columns only when at
    // least one row in THIS section uses them (same predicates the row renderers gate on), so the
    // cells stay consistent all the way down without reserving blank columns in sections that
    // never need them.
    const cols: AbilityRowCols = { effect: false, cost: false };
    for (const e of sorted) {
        const ab = resolveAbilityNest(e, caster);
        if (!ab || typeof ab !== 'object') continue;
        if (ftLayerHas(ab, 'die')) cols.effect = true;
        if (ftLayerHas(ab, 'consumes_ability_charges') || ftLayerHas(ab, 'add_charges_to_ability')) cols.cost = true;
        if (cols.effect && cols.cost) break;
    }
    const rows = sorted.map((e) => buildRow(caster, e, type, canEdit, cols)).join('');
    if (rows === '') return '';
    // Colour-themed per ability TYPE (ft-ability-hdr) — supersedes the old bg-${color}/text-dark
    // utility band so these headers match the list-display section headers. `color` is retained in
    // the signature for callers but no longer drives the header skin.
    void color;
    const hdrClass = abilityHeaderClass(type);
    const titleSpan = `<span class="d-inline-flex align-items-center gap-2">${iconPh(type, 22, 22, false)}${escapeHtml(ucLabel(type))}</span>`;
    // Headers are LEFT-aligned (ps-3 clears the 4px ft-ability-hdr accent bar) so the xl search
    // overlay on the banner's right never collides with the title on narrow hosts (object-card
    // popup, side panels). With a header suffix (the weapons class-mechanic banner) the title
    // leads and the mechanic strips follow it.
    const header = opts.headerSuffixHtml
        ? `<h5 class="d-flex align-items-center mt-1 mb-0 pt-2 pb-2 ps-3 ${hdrClass} rounded-top">`
            + `<span class="d-inline-flex align-items-center gap-2 flex-shrink-0">${iconPh(type, 22, 22, false)}${escapeHtml(ucLabel(type))}</span>`
            + `<span class="d-flex flex-nowrap align-items-center gap-2 ms-3" style="flex:0 1 auto;min-width:0;">${opts.headerSuffixHtml}</span>`
            + `</h5>`
        : `<h5 class="text-start ps-3 mt-1 mb-0 pt-2 pb-2 ${hdrClass} rounded-top">${titleSpan}</h5>`;
    // colgroup mirrors the row's cell set (fixed layout), v5 order: btn | name | damage | source |
    // cost | charges. The effect column reserves room for the widest damage unit ([x]d8 + type
    // select + its side padding), the cost column for a qty badge + pool icon.
    const colgroup = `<colgroup><col style="width:70px;"><col>`
        + (cols.effect ? `<col style="width:190px;">` : '')
        + `<col style="width:clamp(50px,7vw,72px);">`
        + (cols.cost ? `<col style="width:80px;">` : '')
        + `<col style="width:175px;"></colgroup>`;
    return `<div class="fate-tide-sheet-ability-section">
        ${header}
        <div class="table-responsive-xxl mb-3">
            <table class="table table-striped table-hover table-sm" style="table-layout:fixed;width:100%;">
                ${colgroup}
                <tbody>${rows}</tbody>
            </table>
        </div>
    </div>`;
}

/** Build one ability-type section in ICON-ONLY mode: the same colour-themed header as buildSection,
 *  followed by a left-to-right flex-wrap strip of just the ability medallions (the rune button face).
 *  Each medallion is the SAME generateAbilityButton() the table rows use — it already carries the
 *  ability name as its hover title and all the click-to-use handlers — so behaviour is unchanged;
 *  only the surrounding name/source/charges columns are dropped. `size` ('s'|'m'|'l') drives both the
 *  rune LOD and the strip's CSS size class. Returns '' for empty sections (parity with buildSection).
 *  ability_choice parents have no castable medallion, so they fall back to their type chip + name. */
export function buildIconSection(caster: CachedCaster, type: string, canEdit: boolean, size: 's' | 'm' | 'l'): string {
    const entries = abilityEntriesOfType(caster, type);
    if (!entries || entries.length === 0) return '';
    const sorted = sortAbilitySection(caster, type, entries);
    const meds: string[] = [];
    for (const entry of sorted) {
        const ab = recordOf(resolveAbilityNest(entry, caster));
        if (!ab?.item_layer_types) {
            if (!ab || !ab.items) continue; // leaned shell — skip (parity with buildRow)
        }
        const id = abilityEntryId(entry);
        let name = abilityEntryName(entry);
        // Same bare-UUID name fallback as buildRow: when the entry-level resolver misses, read the
        // name off the already-resolved nest `ab` (carries the def's item_name). Decoded here for the
        // same reason as buildRow's copy — this path never passes through abilityEntryName.
        if (name === '' || name === 'Unknown') {
            const nestName = (ab && typeof ab === 'object') ? ftItemsCol(ab, 'item_name') : '';
            if (typeof nestName === 'string' && nestName !== '') name = decodeHtmlEntitiesForPlainText(nestName);
        }
        const searchName = escapeHtml(name.toLowerCase());

        // ability_choice parent: no single castable rune — show its type chip + name as a clickable chip.
        const acOptions = ab.ability_choice_options;
        if (Array.isArray(acOptions) && acOptions.length > 0) {
            meds.push(`<span class="ft-ability-icon fate-tide-sheet-ability-row d-inline-flex align-items-center gap-1" data-ft-ability-name="${searchName}" role="button" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-animation="false" data-bs-original-title="${escapeHtml(name)}" ${abilityInfoAttrs(id)}>${iconPh(type, 22, 22, false)}<span class="small">${escapeHtml(name)}</span></span>`);
            continue;
        }

        const entryRecord = recordOf(entry);
        const source = entryRecord?.source ?? null;
        const spellType = type === 'charged_ability' ? 'charged_ability' : null;
        const btn = generateAbilityButton(ab, caster, {
            spellType,
            source: typeof source === 'string' ? source : null,
            // Entry-level flag (see buildRow) — NOT on the ability nest `ab`.
            consumeNestedUnitPerUse: entryRecord?.consume_nested_unit_per_use === true,
            buttonDisabled: !canEdit,
            iconSize: size,
            bootstrapTooltip: true,
        });
        if (btn) meds.push(`<span class="ft-ability-icon fate-tide-sheet-ability-row" data-ft-ability-name="${searchName}">${btn}</span>`);
    }
    if (meds.length === 0) return '';
    const hdrClass = abilityHeaderClass(type);
    const titleSpan = `<span class="d-inline-flex align-items-center gap-2">${iconPh(type, 22, 22, false)}${escapeHtml(ucLabel(type))}</span>`;
    return `<div class="fate-tide-sheet-ability-section">
        <h5 class="text-start ps-3 mt-1 mb-0 pt-2 pb-2 ${hdrClass} rounded-top">${titleSpan}</h5>
        <div class="ft-ability-iconstrip ft-ai--${size} mb-3">${meds.join('')}</div>
    </div>`;
}
